// @flow strict

import * as React from 'react';
import {
  defaultTo,
  find,
  includes,
  prop,
  propSatisfies,
  sortBy,
  test,
} from 'ramda';
import styles from './strand-grouping.css';
import alStyles from './achievement-level-panel.css';
import { sanitizeIndicator, toKebabCase } from './strand-utils';
import expandLess from './images/ic_expand_less_24px.svg';
import expandMore from './images/ic_expand_more_24px.svg';
import iconStyles from './icons.css';
import svgImageFn from './svg-image.jsx';
import SanitizedHtml from './SanitizedHtml';
import type { AchievementLevelConfig, Skill } from './ald-types'

export type Props = {|
  achievementLevel: AchievementLevelConfig,
  changeStrandDisplayStatus: (string) => void,
  dimmed: boolean,
  nextStrand: string,
  indicatorRowNames: Array<string>,
  skillsVisible: boolean,
  specificSkills: Skill[],
  strand: string,
|};

const ExpandLessIcon = svgImageFn(
  iconStyles.mediumSquare,
  iconStyles.darkIcon,
  expandLess
);
const ExpandMoreIcon = svgImageFn(
  iconStyles.mediumSquare,
  iconStyles.darkIcon,
  expandMore
);

type IndicatorSectionProps = {
  borderColor?: string,
  descriptors: string[],
  dimmedStyle?: string,
  gridRow?: string,
  includeBorder: boolean,
  indicator: string,
  indicatorText: string,
  strandCyValue: string,
};

const defaultToEmptyString = defaultTo('');

const IndicatorSection = ({
  borderColor,
  descriptors,
  dimmedStyle,
  gridRow,
  includeBorder,
  indicator,
  indicatorText,
  strandCyValue,
}: IndicatorSectionProps): React.Node => {
  const indicatorCyValue = sanitizeIndicator(indicator);
  const style = gridRow ? { gridRow, borderColor } : null;
  const borderClasses = includeBorder
    ? `${alStyles.sideBorder} ${defaultToEmptyString(dimmedStyle)}`
    : '';
  return (
    <div
      className={`nwea-ds-v1-typography-body-copy-default ${styles.skill} ${borderClasses}`}
      key={indicator}
      style={style}
      role="article"
    >
      <h4
        className={alStyles.indicatorPanel}
        data-cy={`state-standard-${strandCyValue}-${indicatorCyValue}`}
      >
        <span className={alStyles.indicator}>{indicator}</span>{' '}
        <span data-cy={`indicator-text-${strandCyValue}-${indicatorCyValue}`}>
          <SanitizedHtml unsanitizedHtml={indicatorText} />
        </span>
      </h4>
      {descriptors.map((descriptor) => {
        return (
          // eslint-disable-next-line react/jsx-key
          <p
            className={alStyles.descriptorTxt}
            data-cy={`skill-descriptor-${strandCyValue}-${indicatorCyValue}`}
          >
            <SanitizedHtml unsanitizedHtml={descriptor} />
          </p>
        );
      })}
    </div>
  );
};

type OnGradeGroupingProps = {borderColor:string,
  dimmed:boolean | string,
  dimmedStyle:string,
  indicatorRowNames:Array<string>,
  skillsVisible:boolean,
  specificSkills:Skill[],
  strand:string,
  strandCyValue:string,
  toggleSkills:(SyntheticEvent<HTMLButtonElement>)=>void}

const OnGradeGrouping = ({
  borderColor,
  dimmed,
  dimmedStyle,
  indicatorRowNames,
  skillsVisible,
  specificSkills,
  strand,
  strandCyValue,
  toggleSkills
}:OnGradeGroupingProps): React.Node => {
  const strandToggleIconCyValue = `strand-toggle-icon-${strandCyValue}`;
  return (
    <div
      className={`${alStyles.displayContents}`}
      aria-label={`${strand} Skills`}
      aria-hidden={dimmed}
    >
      <div
        style={{ gridRow: `${toKebabCase(strand)}`, borderColor:borderColor }}
        className={`${alStyles.sideBorder} ${dimmedStyle}`}
        role="heading"
        aria-level="3"
      >
        <button
          aria-label={`${skillsVisible ? 'Hide' : 'Show'} ${strand} Skills`}
          role="button"
          className={`nwea-ds-v1-typography-subtitle ${styles.strand}`}
          data-cy={`strand-toggle-${strandCyValue}`}
          disabled={dimmed}
          onClick={toggleSkills}
          type="button"
          aria-expanded={skillsVisible}
        >
          {strand}
          {skillsVisible ? (
            <ExpandLessIcon
              dataCyTag={strandToggleIconCyValue}
              dataCyStateTag="less"
            />
          ) : (
            <ExpandMoreIcon
              dataCyTag={strandToggleIconCyValue}
              dataCyStateTag="more"
            />
          )}
        </button>
      </div>
      {skillsVisible ? (
        <div className={styles.onGradeSkills} aria-label={`${strand} Skills`}>
          {indicatorRowNames.map((indicatorRowName) => {
            const skill = find(
              propSatisfies(includes(indicatorRowName), 'indicator'),
              specificSkills
            );
            const gridRow = `${toKebabCase(strand)}-${sanitizeIndicator(
              indicatorRowName
            )}`;
            if (!skill) {
              return (
                <IndicatorSection
                  includeBorder={true}
                  borderColor={borderColor}
                  descriptors={[
                    'This standard is not applicable at this achievement level.',
                  ]}
                  dimmedStyle={dimmedStyle}
                  gridRow={gridRow}
                  indicator={indicatorRowName}
                  indicatorText={''}
                  strandCyValue={strandCyValue}
                  key={indicatorRowName}
                />
              );
            } else {
              return (
                <IndicatorSection
                  includeBorder={true}
                  borderColor={borderColor}
                  descriptors={skill.descriptors}
                  dimmedStyle={dimmedStyle}
                  gridRow={gridRow}
                  indicator={skill.indicator}
                  indicatorText={skill.indicatorText}
                  strandCyValue={strandCyValue}
                  key={indicatorRowName}
                />
              );
            }
          })}
        </div>
      ) : null}
    </div>
  );
};

type OffGradeGroupingProps = {borderColor:string,
  dimmed:boolean,
  dimmedStyle:string,
  indicatorRowNames:Array<string>,
  nextStrand:string,
  skillsVisible:boolean,
  specificSkills:Skill[],
  strand:string,
  strandCyValue:string,
  toggleSkills:(SyntheticEvent<HTMLButtonElement>)=>void}

const OffGradeGrouping = ({
  borderColor,
  dimmed,
  dimmedStyle,
  indicatorRowNames,
  nextStrand,
  skillsVisible,
  specificSkills,
  strand,
  strandCyValue,
  toggleSkills
}:OffGradeGroupingProps): React.Node => {
  const sortedSkills = sortBy(prop('indicator'))(specificSkills);
  const strandToggleGridRow = toKebabCase(strand);
  const gridRowStart = `${toKebabCase(strand)}-${sanitizeIndicator(
    indicatorRowNames[0]
  )}`;
  const gridRowEnd = toKebabCase(nextStrand);
  const strandToggleIconCyValue = `strand-toggle-icon-${strandCyValue}`;
  return (
    <div
      className={`${alStyles.displayContents}`}
      aria-label={`${strand} Skills`}
      aria-hidden={dimmed}
    >
      <div
        style={{ gridRow: strandToggleGridRow, borderColor: borderColor}}
        className={`${alStyles.sideBorder} ${dimmedStyle}`}
        role="heading"
        aria-level="3"
      >
        <button
          aria-label={`${skillsVisible ? 'Hide' : 'Show'} ${strand} Skills`}
          role="button"
          className={`nwea-ds-v1-typography-subtitle ${styles.strand}`}
          data-cy={`strand-toggle-${strandCyValue}`}
          disabled={dimmed}
          onClick={toggleSkills}
          type="button"
          aria-expanded={skillsVisible}
        >
          {strand}
          {skillsVisible ? (
            <ExpandLessIcon
              dataCyTag={strandToggleIconCyValue}
              dataCyStateTag="less"
            />
          ) : (
            <ExpandMoreIcon
              dataCyTag={strandToggleIconCyValue}
              dataCyStateTag="more"
            />
          )}
        </button>
      </div>
      {skillsVisible ? (
        <div
          style={{ gridRow: `${gridRowStart} / span ${gridRowEnd}`, borderColor: borderColor }}
          className={`${styles.offGradeSkills} ${alStyles.sideBorder} ${dimmedStyle}`}
          aria-label={`${strand} Skills`}
        >
          {/* eslint-disable-next-line react/jsx-key */}
          {sortedSkills.map((skill) => (
            // eslint-disable-next-line react/jsx-key
            <IndicatorSection
              includeBorder={false}
              descriptors={skill.descriptors}
              indicator={skill.indicator}
              indicatorText={skill.indicatorText}
              strandCyValue={strandCyValue}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};

const StrandGrouping = (props: Props): React.Node => {
  const toggleSkills = (event:SyntheticEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    props.changeStrandDisplayStatus(props.strand);
  };

  const borderColor = props.achievementLevel.color
  const strandCyValue = toKebabCase(
    `${props.achievementLevel.name}-${props.strand}`
  );
  const dimmedStyle = props.dimmed ? alStyles.dim : '';

  if (props.achievementLevel.offGrade) {
    return (
      <OffGradeGrouping
        borderColor={borderColor}
        dimmed={props.dimmed}
        dimmedStyle={dimmedStyle}
        nextStrand={props.nextStrand}
        indicatorRowNames={props.indicatorRowNames}
        skillsVisible={props.skillsVisible}
        specificSkills={props.specificSkills}
        strand={props.strand}
        strandCyValue={strandCyValue}
        toggleSkills={toggleSkills}
      />
    );
  } else {
    return (
      <OnGradeGrouping
        borderColor={borderColor}
        dimmed={props.dimmed}
        dimmedStyle={dimmedStyle}
        nextStrand={props.nextStrand}
        indicatorRowNames={props.indicatorRowNames}
        skillsVisible={props.skillsVisible}
        specificSkills={props.specificSkills}
        strand={props.strand}
        strandCyValue={strandCyValue}
        toggleSkills={toggleSkills}
      />
    );
  }
};

export default StrandGrouping;
